import React, { useEffect, useState } from 'react';

const InstagramBrowserDetector = ({ children }) => {
  const [showOpenInBrowser, setShowOpenInBrowser] = useState(false);
  const [isInstagramBrowser, setIsInstagramBrowser] = useState(false);

  useEffect(() => {
    // Prevent showing overlay if redirect was already attempted
    const REDIRECT_ATTEMPT_KEY = 'bela_redirect_attempted';
    const redirectAttempted = sessionStorage.getItem(REDIRECT_ATTEMPT_KEY);
    
    // Function to detect if we're in Instagram's in-app browser
    const detectInstagramBrowser = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      
      // Check for Instagram's in-app browser patterns
      const isInstagram = /Instagram/.test(userAgent) || 
                         /FBAN|FBAV/.test(userAgent) || // Facebook/Instagram app
                         /FB_IAB/.test(userAgent) || // Facebook In-App Browser
                         window.location.search.includes('ig_web_button_share_sheet');
      
      // Additional check for iOS Instagram
      const isIOSInstagram = /iPhone|iPad/.test(userAgent) && /Instagram/.test(userAgent);
      
      // Additional check for Android Instagram
      const isAndroidInstagram = /Android/.test(userAgent) && /Instagram/.test(userAgent);
      
      return isInstagram || isIOSInstagram || isAndroidInstagram;
    };

    // Function to detect other problematic in-app browsers
    const detectOtherInAppBrowsers = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      
      // TikTok, Twitter, LinkedIn, etc.
      return /TikTok|TwitterAndroid|LinkedInApp|WhatsApp|Telegram/.test(userAgent);
    };

    const inInstagramBrowser = detectInstagramBrowser();
    const inOtherInAppBrowser = detectOtherInAppBrowsers();
    
    setIsInstagramBrowser(inInstagramBrowser || inOtherInAppBrowser);
    
    // Only show overlay manually if we're in in-app browser but DON'T auto-attempt redirect
    // The early script in index.html handles the initial redirect attempt
    if ((inInstagramBrowser || inOtherInAppBrowser) && !redirectAttempted) {
      // Don't auto-show or auto-redirect, let the early script handle it
      // This component is now just a fallback for manual user action
      setShowOpenInBrowser(false);
    } else if (redirectAttempted) {
      // If redirect was already attempted, don't show overlay automatically
      setShowOpenInBrowser(false);
    }
  }, []);

  const attemptExternalBrowserOpen = () => {
    // Mark that we attempted redirect to prevent loops
    const REDIRECT_ATTEMPT_KEY = 'bela_redirect_attempted';
    sessionStorage.setItem(REDIRECT_ATTEMPT_KEY, 'true');
    
    const currentUrl = window.location.href;
    
    // Try different methods to open in external browser
    // Method 1: Use intent:// scheme for Android
    if (/Android/.test(navigator.userAgent)) {
      const intentUrl = `intent://${window.location.host}${window.location.pathname}${window.location.search}#Intent;scheme=https;package=com.android.chrome;end`;
      window.location.href = intentUrl;
      // Removed the setTimeout fallback to prevent multiple attempts
    }
    // Method 2: For iOS, try to open in Safari
    else if (/iPhone|iPad/.test(navigator.userAgent)) {
      // iOS doesn't allow direct opening of external browsers from in-app browsers
      // We'll show instructions instead
      setShowOpenInBrowser(true);
    }
    // Method 3: For other platforms, try opening in new window
    else {
      const newWindow = window.open(currentUrl, '_blank');
      if (!newWindow) {
        setShowOpenInBrowser(true);
      }
    }
  };

  const handleOpenInBrowser = () => {
    const currentUrl = window.location.href;
    
    // Copy URL to clipboard
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(currentUrl).then(() => {
        alert('URL copied to clipboard! Please paste it in your browser.');
      });
    }
    
    // Try to open in external browser
    attemptExternalBrowserOpen();
  };

  const handleDismiss = () => {
    setShowOpenInBrowser(false);
  };

  // If we're in an in-app browser, show the overlay
  if (showOpenInBrowser) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6 text-center">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Better Experience Available
          </h3>
          
          <p className="text-gray-600 mb-6">
            For the best experience with Bela, please open this link in your device's default browser instead of the {isInstagramBrowser ? 'Instagram' : 'social media'} app.
          </p>
          
          <div className="space-y-3">
            <button
              onClick={handleOpenInBrowser}
              className="w-full bg-pink-500 hover:bg-pink-600 text-white font-medium py-3 px-4 rounded-lg transition-colors"
            >
              Open in Browser
            </button>
            
            <button
              onClick={handleDismiss}
              className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg transition-colors"
            >
              Continue Here
            </button>
          </div>
          
          <div className="mt-4 text-sm text-gray-500">
            <p className="mb-2">To open in your browser:</p>
            <div className="text-left space-y-1">
              <p>• Tap the three dots (•••) in the top right</p>
              <p>• Select "Open in Browser" or "Open in Safari/Chrome"</p>
              <p>• Or copy and paste this URL in your browser</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If not in in-app browser or user dismissed, render children normally
  return children;
};

export default InstagramBrowserDetector;
